import { Card, Divider, Pagination, Table, Typography, Tooltip, message, Space, Modal, Input } from 'antd';
import { FullscreenOutlined } from '@ant-design/icons';
import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import styled from 'styled-components';
import moment from 'moment';
import SearchFilter from '../../component/Filter';
import { useAppDispatch, useAppSelector } from '../../methods/redux/hooks';
import { retrieveAdminUnremittedBalances } from '../../methods/redux/reducers/admin-unremitted-balances';
import { getCurrencies } from '../../methods/redux/reducers/currency';
import remakeQueryString from '../../methods/utils/remake-query-string';
import NativeNumber from '../../component/Numeral';
import { commas, truncateAddress } from '../../methods/utils/general-utils';
import TableHeaderSection from '../../component/TableHeaderSection';
import getExplorerLink from '../../compliance/request-logs/getExplorerLink';
import { hideEmail } from '../../methods/utils/request-helpers';

const { Title, Text } = Typography;

export const PageWrapper = styled.div`
	display: flex;
	flex-direction: column;
	width: 100%;
	height: 100%;
	gap: 1em;

	& .myCard {
		border-radius: 10px;
		gap: 1em;
	}
`;

const UnremittedBalances = () => {
	const dispatch = useAppDispatch();
	const [searchParams, setSearchParams] = useSearchParams();
	const [pagination, setPagination] = useState({
		current: 1,
		pageSize: 20,
	});
	const [isUnhashed, setIsUnhashed] = useState(false);
	const [isPasscodeModalVisible, setIsPasscodeModalVisible] = useState(false);
	const [passcode, setPasscode] = useState('');

	const { unremittedBalances } = useAppSelector((store: any) => store.adminUnremittedBalances);
	const { currencies } = useAppSelector((store: any) => store.currencies);

	const headers = [
		{ label: 'Full Name', key: 'fullName' },
		{ label: 'Username', key: 'username' },
		{ label: 'User Email', key: 'userEmail' },
		{ label: 'Wallet Address', key: 'walletAddress' },
		{ label: 'Contract Address', key: 'contractAddress' },
		{ label: 'Chain Id', key: 'chainId' },
		{ label: 'Chain Name', key: 'chainName' },
		{ label: 'Currency Name', key: 'currencyName' },
		{ label: 'Currency Symbol', key: 'currencySymbol' },
		{ label: 'Decimal', key: 'decimals' },
		{ label: 'Balance', key: 'balance' },
		{ label: 'Balance In USD', key: 'balanceInUSD' },
		{ label: 'Created At', key: 'createdAt' },
	];

	useEffect(() => {
		const queryData = Object.fromEntries(searchParams.entries());
		if (Object.keys(queryData).length === 0) {
			setSearchParams({
				pageId: '1',
				perPage: String(pagination.pageSize),
			});
		}

		dispatch(getCurrencies({}));
	}, []);

	const updateQueryString = (query: any, action = 'update') => {
		remakeQueryString(query, searchParams, setSearchParams, action);
	};

	const handlePasscodeSubmit = () => {
		if (passcode === '2026Xend') {
			setIsUnhashed(true);
			setIsPasscodeModalVisible(false);
			setPasscode('');
			message.success('Emails unhashed successfully');
		} else {
			message.error('Incorrect passcode');
		}
	};

	useEffect(() => {
		const queryData = Object.fromEntries(searchParams.entries());
		if (Object.keys(queryData).length > 0) {
			dispatch(retrieveAdminUnremittedBalances(queryData));
		}
	}, [searchParams]);

	const currencySelect = currencies.map((item: any) => ({
		name: item.symbol,
		value: item.symbol,
	}));

	const chainIdSelect = [...new Set(unremittedBalances.summary?.breakdown?.map((item: any) => item.chainId))]
		.filter((id: any) => id !== undefined && id !== null)
		.map((chainId: any) => ({
			name: chainId,
			value: chainId,
		}));

	const columns = [
		{
			title: 'S/N',
			dataIndex: 'number',
			key: 'number',
			render: (_: any, __: any, index: number) => (
				<strong>
					{(pagination.current - 1) * pagination.pageSize + index + 1}
				</strong>
			),
		},
			{
			title: 'Full Name',
			dataIndex: 'userId',
			key: 'userId',
			render: (userId: any, record: any) => (
				<div style={{ display: 'flex', alignItems: 'center' }}>
					<Text strong>{userId.fullName}</Text>
				</div>
			),
		},

		{
			title: 'Username',
			dataIndex: 'userId',
			key: 'userId',
			render: (userId: any, record: any) => (
				<div style={{ display: 'flex', alignItems: 'center' }}>
					<Text strong>{userId.username}</Text>
				</div>
			),
		},

				{
			title: 'User Email',
			dataIndex: 'userEmail',
			key: 'userEmail',
			render: (userEmail: any, record: any) => (
				<div style={{ display: 'flex', alignItems: 'center' }}>
					<a href={`/user-profile/${record.userId._id}`}><Text strong>{isUnhashed ? userEmail : hideEmail(userEmail)}</Text></a>
				</div>
			),
		},
			{
			title: 'Wallet Address',
			dataIndex: 'walletAddress',
			key: 'walletAddress',
			render: (walletAddress: any, record: any) => (
				<div style={{ display: 'flex', alignItems: 'center' }}>
					<Text strong>{walletAddress}</Text>
				</div>
			),
		},
			{
			title: 'Contract Address',
			dataIndex: 'contractAddress',
			key: 'contractAddress',
			render: (contractAddress: any, record: any) => (
				<div style={{ display: 'flex', alignItems: 'center' }}>
					<Text strong>{contractAddress}</Text>
				</div>
			),
		},
		{
			title: 'Chain Id',
			dataIndex: 'chainId',
			key: 'chainId',
			render: (chainId: any, record: any) => (
				<div style={{ display: 'flex', alignItems: 'center' }}>
					{record.iconUrl && <img src={record.iconUrl} alt="icon" style={{ width: 20, height: 20, marginRight: 8, borderRadius: '50%' }} />}
					<Text strong>{chainId}</Text>
				</div>
			),
		},
		{
			title: 'Chain Name',
			dataIndex: 'chainName',
			key: 'chainName',
			render: (chainName: any, record: any) => (
				<div style={{ display: 'flex', alignItems: 'center' }}>
					{record.iconUrl && <img src={record.iconUrl} alt="icon" style={{ width: 20, height: 20, marginRight: 8, borderRadius: '50%' }} />}
					<Text strong>{chainName}</Text>
				</div>
			),
		},
			{
			title: 'Explorer',
			dataIndex: 'blockchainNetworkId',
			key: 'blockchainNetworkId',
			render: (blockchainNetworkId: any, record: any) => (
	
				<Tooltip title="View on Explorer" color="blue">
					<Link
						to={getExplorerLink(blockchainNetworkId?.abbreviation, record.walletAddress)}
						target="_blank"
						rel="noreferrer"
					>
						<FullscreenOutlined />
					</Link>
				</Tooltip>
			),
		},
			{
			title: 'Currency Name',
			dataIndex: 'currencyName',
			key: 'currencyName',
			render: (currencyName: any, record: any) => (
				<div style={{ display: 'flex', alignItems: 'center' }}>
					{record.iconUrl && <img src={record.iconUrl} alt="icon" style={{ width: 20, height: 20, marginRight: 8, borderRadius: '50%' }} />}
					<Text strong>{currencyName}</Text>
				</div>
			),
		},
				{
			title: 'Decimal',
			dataIndex: 'decimals',
			key: 'decimals',
			render: (decimals: any, record: any) => (
				<div style={{ display: 'flex', alignItems: 'center' }}>
					<Text strong>{decimals}</Text>
				</div>
			),
		},
		{
			title: 'Currency Symbol',
			dataIndex: 'currencySymbol',
			key: 'currencySymbol',
			render: (currencySymbol: any, record: any) => (
				<div style={{ display: 'flex', alignItems: 'center' }}>
					{record.iconUrl && <img src={record.iconUrl} alt="icon" style={{ width: 20, height: 20, marginRight: 8, borderRadius: '50%' }} />}
					<Text strong>{currencySymbol}</Text>
				</div>
			),
		},
		{
			title: 'Total Balance',
			dataIndex: 'balance',
			key: 'balance',
			render: (balance: any) => (
				<Text strong>
					<NativeNumber value={balance} />
				</Text>
			),
		},
		{
			title: 'Balance In USD',
			dataIndex: 'balanceInUSD',
			key: 'balanceInUSD',
			render: (balanceInUSD: any) => (
				<Text strong>
					$ <NativeNumber value={balanceInUSD} />
				</Text>
			),
		},
		{
			title: 'Created At',
			dataIndex: 'createdAt',
			key: 'createdAt',
			render: (createdAt: string) => (
				<Text>{moment(createdAt).format('MMM DD, YY / hh:mm:ss a')}</Text>
			),
		},
	];

	return (
		<PageWrapper>
			<Card>
				<Title level={3}>Admin Unremitted Balances</Title>
				<Text>
					This page displays the list of unremitted balances for administrators.
				</Text>
				<Divider />
				<h1>Total Unremitted balance In USD: $ {Number(unremittedBalances.summary?.totalUSDValue).toLocaleString()}</h1>

				<div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px', marginTop: '16px' }}>
					<button
						className="ant-btn ant-btn-primary"
						onClick={() => {
							if (isUnhashed) {
								setIsUnhashed(false);
								message.success('Emails hashed');
							} else {
								setIsPasscodeModalVisible(true);
							}
						}}
					>
						{isUnhashed ? 'Hash Emails' : 'Unhash Emails'}
					</button>
				</div>

				<Divider />

				<SearchFilter
					title="Filter by Currency"
					searchFilters={[
						{
							label: 'Currency',
							name: 'currencySymbol',
							htmlElement: 'select',
							selectOptions: currencySelect,
						},
						{
							label: 'Chain Id',
							name: 'chainId',
							htmlElement: 'select',
							selectOptions: chainIdSelect,
						},
						{
							label: 'User Email',
							name: 'userEmail',
							htmlElement: 'input',
						},
						{
							label: 'Wallet Address',
							name: 'walletAddress',
							htmlElement: 'input',
						},
						{
							label: 'Is Native Token?',
							name: 'isNativeToken',
							htmlElement: 'select',
							selectOptions: [
								{ name: 'Yes', value: 'true' },
								{ name: 'No', value: 'false' },
							],
						},
						{
							label: 'Sort By',
							name: 'sortBy',
							htmlElement: 'select',
							selectOptions: [
								{ name: 'Balance', value: 'balance' },
								{ name: 'Balance In USD', value: 'balanceInUSD' },
							],
						},
						{
							label: 'Sort Order',
							name: 'sortOrder',
							htmlElement: 'select',
							selectOptions: [
								{ name: 'Ascending', value: 'asc' },
								{ name: 'Descending', value: 'desc' },
							],
						},
					]}
					retrieveQueryString={updateQueryString}
				/>

				<Divider />

				<TableHeaderSection
					tableName="Total Unremitted Balances Count"
					currentPage={Number(searchParams.get('pageId')) || 1}
					// totalRecords={unremittedBalances.details?.paginator?.itemCount || 21}
					totalRecords={unremittedBalances.paginator?.itemCount || 0}
					downloadURL="/admin/unremitted-balances"
					csvHeaders={headers}
					isUnhashed={isUnhashed}
					csvDataTransformer={(items: any[]) =>
						items.map((item: any) => ({
							...item,
							fullName: item.userId?.fullName || 'N/A',
							username: item.userId?.username || 'N/A',
							userEmail: isUnhashed ? item.userEmail : hideEmail(item.userEmail),
							createdAt: moment(item.createdAt).format('MMM DD, YY / hh:mm:ss a'),
						}))
					}
				/>

				<Table
					dataSource={unremittedBalances.itemsList || []}
					columns={columns}
					rowKey="_id"
					pagination={false}
					scroll={{ x: 'max-content' }}
				/>

				<Divider />

				<Pagination
					onChange={(pageId, pageSize) => {
						updateQueryString({ pageId, perPage: pageSize });
						setPagination({ current: pageId, pageSize: pageSize || 20 });
					}}
					 total={unremittedBalances.paginator?.itemCount || 0}
					current={Number(searchParams.get('pageId')) || 1}
					pageSize={Number(searchParams.get('perPage')) || 20}
					showSizeChanger
					showQuickJumper
				/>

				<Modal
					title="Enter Passcode to Unhash Emails"
					visible={isPasscodeModalVisible}
					onOk={handlePasscodeSubmit}
					onCancel={() => setIsPasscodeModalVisible(false)}
				>
					<Input.Password
						placeholder="Enter passcode"
						value={passcode}
						onChange={(e) => setPasscode(e.target.value)}
						onPressEnter={handlePasscodeSubmit}
					/>
				</Modal>
			</Card>
		</PageWrapper>
	);
};

export default UnremittedBalances;
